import { Dialog, DialogContent, DialogTitle } from '@/Components/Dialog/Dialog';
import type TextBlockModel from '@/Models/TextBlock/TextBlockModel';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { DobTableWrapper } from '@dedalusdiit/dob-components';

const columnHelper = createColumnHelper<TextBlockModel>();

const columns = [
    columnHelper.accessor('Name', {
        header: 'Name',
        cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('SearchTerm', {
        header: 'SearchTerm',
        cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('Pronunciation', {
        header: 'Pronunciation',
        cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('Content', {
        header: 'Content',
        cell: (info) => {
            const doc = new DOMParser().parseFromString(info.getValue(), 'text/html');
            return doc.body.textContent?.substring(0, 40) ?? '';
        },
    }),
    columnHelper.accessor('Language', {
        header: 'Language',
        cell: (info) => info.getValue(),
    }),
];

export interface AutoInsertTextBlocksProps {
    textBlocks: TextBlockModel[];
    onClick: Function;
    onCancel: Function;
}

function AutoInsertTextBlocks(props: Readonly<AutoInsertTextBlocksProps>): React.ReactElement {
    const { t } = useTranslation();
    const { textBlocks, onClick, onCancel } = props;

    const table = useReactTable({
        data: textBlocks,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <Dialog open={true}>
            <DialogTitle onClose={() => onCancel()}>{t('ChooseTextblockToInsert')}</DialogTitle>
            <DialogContent>
                <DobTableWrapper color="primary">
                    <table>
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th key={header.id}>
                                            <div>{flexRender(t(header.column.columnDef.header as string), header.getContext())}</div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => (
                                <tr key={row.id} onClick={(_) => onClick(row.original)}>
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </DobTableWrapper>
            </DialogContent>
        </Dialog>
    );
}

export default AutoInsertTextBlocks;
